import { Injectable } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { environment } from '../../../environments/environment';

/**
 * Service for logging to Application Insights
 */
@Injectable({
  providedIn: 'root'
})
export class LoggingService {
  private appInsights: ApplicationInsights | null = null;

  constructor() {
    this.initializeAppInsights();
  }

  /**
   * Initialize Application Insights
   */
  private initializeAppInsights(): void {
    if (environment.appInsights?.connectionString) {
      this.appInsights = new ApplicationInsights({
        config: {
          connectionString: environment.appInsights.connectionString,
          enableAutoRouteTracking: true,
          enableCorsCorrelation: false, // Disabled to prevent CORS issues with Auth0
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
        }
      });

      this.appInsights.loadAppInsights();
      this.appInsights.trackPageView();
    } else {
      console.warn('Application Insights connection string not configured');
    }
  }

  /**
   * Track an exception
   */
  trackException(exception: Error, properties?: Record<string, any>): void {
    if (this.appInsights) {
      this.appInsights.trackException({
        exception,
        properties
      });
    } else {
      console.error('Exception:', exception, properties);
    }
  }

  /**
   * Track a trace message
   */
  trackTrace(message: string, properties?: Record<string, any>): void {
    if (this.appInsights) {
      this.appInsights.trackTrace({
        message,
        properties
      });
    } else {
      console.log('Trace:', message, properties);
    }
  }

  /**
   * Track a custom event
   */
  trackEvent(name: string, properties?: Record<string, any>): void {
    if (this.appInsights) {
      this.appInsights.trackEvent({
        name,
        properties
      });
    } else {
      console.log('Event:', name, properties);
    }
  }

  /**
   * Track a page view
   */
  trackPageView(name?: string, uri?: string): void {
    if (this.appInsights) {
      this.appInsights.trackPageView({
        name,
        uri
      });
    }
  }
}
